import styled, { css } from "styled-components";

interface ProfileCardItemStyleProps {
  $data?: boolean;
  $pinned?: boolean;
}

export const ProfileCardItem = styled.li<ProfileCardItemStyleProps>`
  display: flex;
  width: 240px;
  height: 320px;
  border-radius: 16px;
  box-shadow: ${(props) => props.theme.shadows.item};
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  flex-shrink: 0;
  gap: 20px;
  transition: all 0.2s ease;

  ${(props) =>
    props.$data === true &&
    css`
      background: ${props.$pinned
        ? props.theme.colors.primary100
        : props.theme.colors.white100};
    `}

  ${(props) =>
    props.$data === false &&
    css`
      background: ${props.theme.colors.black12};
    `}

    ${(props) =>
    props.$pinned === true &&
    css`
      margin-bottom: 80px;
      box-shadow: ${props.theme.shadows.profile};
    `}

  // 모바일 환경(600px 이하) 설정
  @media (max-width: 600px) {
    width: 100%;
    height: 80px;
    padding: 0 20px;
    justify-content: space-between;
    flex-direction: row;

    ${(props) =>
      props.$pinned === true &&
      css`
        margin-bottom: 0;
        box-shadow: ${props.theme.shadows.profile};
      `}
  }

  // 모바일 가로 모드 대응
  @media (orientation: landscape) {
    width: 100%;
    height: 100px;
    padding: 10px 0;
    justify-content: space-between;
    flex-direction: row;
    padding: 0 20px;

    ${(props) =>
      props.$pinned === true &&
      css`
        margin-bottom: 0;
        box-shadow: ${props.theme.shadows.profile};
      `}
  }

  // 태블릿 환경 (768px 이상) 설정
  @media (min-width: 768px) {
    width: 100%;
    height: 160px;
    flex-direction: row;
    padding: 0 20px;

    ${(props) =>
      props.$pinned === true &&
      css`
        margin-bottom: 0;
        box-shadow: ${props.theme.shadows.profile};
      `}
  }
`;

export const ProfileContent = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;

  // 모바일 가로 모드 대응
  @media (max-height: 500px) and (orientation: landscape) {
    justify-content: space-between;
  }
`;

export const ProfileImg = styled.div<ProfileCardItemStyleProps>`
  position: relative;
  display: flex;
  width: 180px;
  height: 180px;
  background: ${(props) => props.theme.colors.white100};
  border-radius: 50%;
  align-items: center;
  justify-content: center;

  & > svg {
    path {
      fill: ${(props) => props.theme.colors.black12};
    }
  }

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }

  ${(props) =>
    props.$data === false &&
    css`
      padding: 8px;
    `}

  // 모바일 환경(600px 이하) 설정
  @media (max-width: 600px) {
    width: 52px;
    height: 52px;

    & > svg {
      width: 80px;
      height: 80px;
    }

    ${(props) =>
      props.$data === false &&
      css`
        padding: 4px;
      `}
  }

  // 태블릿 환경 (768px 이상) 설정
  @media (min-width: 768px) {
    width: 120px;
    height: 120px;

    & > svg {
      width: 120px;
      height: 120px;
    }
  }

  // 모바일 가로 모드 대응
  @media (orientation: landscape) {
    width: 80px;
    height: 80px;

    & > svg {
      width: 90px;
      height: 90px;
    }
  }
`;

export const ProfileAddIcon = styled.span`
  position: absolute;
  right: 0;
  bottom: -4px;

  & > svg {
    background: ${(props) => props.theme.colors.white100};
    border-radius: 50%;

    path {
      fill: ${(props) => props.theme.colors.white};
    }
  }

  // 모바일 환경(600px 이하) 설정
  @media (max-width: 600px) {
    & > svg {
      width: 16px;
      height: 16px;
    }
  }
  // 모바일 가로 모드 대응
  @media (orientation: landscape) {
    & > svg {
      width: 40px;
      height: 40px;
    }
  }
`;

export const ProfileInfo = styled.div<ProfileCardItemStyleProps>`
  display: flex;
  min-height: 60px;
  color: ${(props) =>
    props.$pinned === true
      ? props.theme.colors.white100
      : props.theme.colors.black100};
  font-size: 2.5rem;
  font-weight: ${(props) => (props.$pinned === true ? "800" : "600")};
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;

  // 모바일 환경(600px 이하) 설정
  @media (max-width: 600px) {
    font-size: 1.6rem;
    align-items: flex-start;
  }

  // 모바일 가로모드 대응
  @media (orientation: landscape) {
    align-items: flex-start;
  }

  // 태블릿 환경 (768px 이상) 설정
  @media (min-width: 768px) {
    align-items: flex-start;
    font-size: 2.8rem;
  }
`;

export const ProfileInfoDetail = styled.div<ProfileCardItemStyleProps>`
  display: flex;
  color: ${(props) =>
    props.$pinned === true
      ? props.theme.colors.white100
      : props.theme.colors.black38};
  font-size: 1.6rem;
  font-weight: 500;
  letter-spacing: 0;
  align-items: center;
  gap: 14px;

  span {
    &:last-of-type {
      padding-left: 14px;
      border-left: 1px solid
        ${(props) =>
          props.$pinned === true
            ? props.theme.colors.white100
            : props.theme.colors.black38};
    }
  }

  // 모바일 환경(600px 이하) 설정
  @media (max-width: 600px) {
    font-size: 1.4rem;
  }

  // 태블릿 환경 (768px 이상) 설정
  @media (min-width: 768px) {
    font-size: 2.2rem;
  }
`;

export const ProfileNoData = styled.span`
  color: ${(props) => props.theme.colors.black60};
  letter-spacing: 0;
`;

export const IconArrow = styled.span<ProfileCardItemStyleProps>`
  svg {
    path {
      fill: ${(props) =>
        props.$pinned === true
          ? props.theme.colors.white100
          : props.theme.colors.black38};
    }
  }
  // 모바일 가로모드 대응
  @media (orientation: landscape) {
    svg {
      width: 28px;
      height: 28px;
    }
  }
  // 태블릿 환경 (768px 이상) 설정
  @media (min-width: 768px) {
    svg {
      width: 28px;
      height: 28px;
    }
  }
`;
